import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

const BRAND = "#154E93";
const GOLD = "#9C7C2C";

/** Grid of pages grouped by department; each page: No access / View only / Can edit. */
function PermissionGrid({ registry, value, onChange }) {
  const set = (key, level) => { const next = { ...value }; if (level) next[key] = level; else delete next[key]; onChange(next); };
  const setDept = (dept, level) => { const next = { ...value }; for (const p of registry.pages.filter((x) => x.dept === dept)) { if (level) next[p.key] = level; else delete next[p.key]; } onChange(next); };
  const opt = (key, level, label, tone) => {
    const on = (value[key] || "") === level;
    return (
      <button type="button" onClick={() => set(key, level)} className={`px-2.5 py-1 text-xs font-semibold border first:rounded-l-lg last:rounded-r-lg -ml-px ${on ? tone : "bg-white text-gray-600 hover:bg-gray-50"}`}>{label}</button>
    );
  };
  return (
    <div className="space-y-4">
      {registry.departments.map((d) => {
        const pages = registry.pages.filter((p) => p.dept === d.id);
        return (
          <div key={d.id} className="border rounded-xl overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b">
              <div className="font-semibold text-sm" style={{ color: "#1B3A6B" }}>{d.label}</div>
              <div className="flex gap-3 text-xs">
                <button type="button" onClick={() => setDept(d.id, "view")} className="underline" style={{ color: BRAND }}>All view</button>
                <button type="button" onClick={() => setDept(d.id, "edit")} className="underline" style={{ color: BRAND }}>All edit</button>
                <button type="button" onClick={() => setDept(d.id, "")} className="underline text-gray-500">None</button>
              </div>
            </div>
            <div className="divide-y">
              {pages.map((p) => (
                <div key={p.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2">
                  <div><div className="text-sm font-medium">{p.label}</div>{p.note && <div className="text-xs text-gray-500">{p.note}</div>}</div>
                  <div className="flex shrink-0">
                    {opt(p.key, "", "No access", "bg-gray-700 text-white border-gray-700")}
                    {opt(p.key, "view", "View only", "bg-amber-500 text-white border-amber-500")}
                    {opt(p.key, "edit", "Can edit", "bg-emerald-600 text-white border-emerald-600")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** "Also works as a caregiver" toggle + which employee record they work under. */
function CaregiverHat({ options, value, onChange, currentEmployeeId }) {
  const choices = (options?.employees || []).filter((e) => !e.linked_email || e.employee_id === currentEmployeeId);
  return (
    <div className="border rounded-xl p-3 bg-amber-50/50" style={{ borderColor: "#E8D9A8" }}>
      <label className="flex items-start gap-2 text-sm font-medium">
        <input type="checkbox" className="mt-1" checked={value.also_caregiver} onChange={(e) => onChange({ ...value, also_caregiver: e.target.checked })} />
        <span>Also works as a caregiver<span className="block text-xs font-normal text-gray-600">Adds "My Caregiver Work" (own shifts, EVV clock-in, visit notes, mileage, timesheets, training, new hire packet) to this login. Office permissions above are unaffected.</span></span>
      </label>
      {value.also_caregiver && (
        <label className="block text-sm mt-2">Employee record they work under
          <select value={value.employee_id || ""} onChange={(e) => onChange({ ...value, employee_id: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 bg-white">
            <option value="">Select…</option>
            {choices.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.name} · {e.position || e.staff_category || ""} · {e.employee_id}</option>)}
          </select>
          <span className="block text-xs text-gray-500 mt-1">Employees who already have their own caregiver login aren't listed — use "Give office access to an existing caregiver login" for them.</span>
        </label>
      )}
    </div>
  );
}

const summary = (registry, perms) => {
  const n = Object.keys(perms).length;
  if (!n) return "No access";
  const edit = Object.values(perms).filter((v) => v === "edit").length;
  return `${n} page${n === 1 ? "" : "s"} · ${edit} can edit · ${n - edit} view only`;
};

const PRESETS = {
  "Scheduler": { clients: "view", caregivers: "view", scheduling: "edit", operations: "edit", evv: "edit", communications: "edit" },
  "HR / Recruiter": { caregivers: "edit", recruiting: "edit", new_hire_packets: "edit", hr_management: "edit", training: "edit", compliance_dashboard: "view", tb_testing: "edit", dementia_training: "edit" },
  "Billing / Finance": { clients: "view", billing: "edit", accounting: "edit", reports: "view", payroll: "edit", mileage: "edit" },
  "Compliance / QA": { clients: "view", caregivers: "view", quality_center: "edit", inspector_access: "edit", progress_notes: "edit", visit_verification: "edit", dementia_training: "edit", tb_testing: "edit", qa_meetings: "edit", pca_supervision: "edit", compliance_dashboard: "view" },
};

export default function StaffAccessManager() {
  const [registry, setRegistry] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", permissions: {}, also_caregiver: false, employee_id: "", from_caregiver: "" });
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(null); // { id, name, permissions }
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.access.registry(), api.access.users(), api.access.caregiverOptions()]).then(([r, u, o]) => { setRegistry(r); setUsers(u); setOptions(o); }).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setNotice("");
    try {
      if (form.from_caregiver) {
        const r = await api.access.fromCaregiver({ user_id: Number(form.from_caregiver), permissions: form.permissions });
        setNotice(`${r.name} (${r.email}) now has office access as well as their caregiver portal. Their password is unchanged.`);
      } else {
        const r = await api.access.create(form);
        setCreds({ name: form.name, email: r.email, password: r.temp_password, emailed: r.email_sent });
      }
      setForm({ name: "", email: "", phone: "", permissions: {}, also_caregiver: false, employee_id: "", from_caregiver: "" }); setCreating(false); await load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    setBusy(true); setError("");
    try { await api.access.setPermissions(editing.id, editing.permissions); await api.access.setCaregiver(editing.id, { also_caregiver: editing.also_caregiver, employee_id: editing.employee_id || null }); setNotice(`Access updated for ${editing.name}. It applies immediately.`); setEditing(null); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!registry) return <div className="text-sm text-gray-500">{error || "Loading…"}</div>;

  return (
    <section className="bg-white border rounded-xl shadow-sm mb-8">
      <div className="px-5 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>ADMINISTRATION</div>
          <h2 className="text-xl font-bold" style={{ color: BRAND }}>Office staff &amp; page access</h2>
          <p className="text-sm text-gray-500">Create logins for office staff and choose, page by page, what each person can view or edit. Changes apply immediately.</p>
        </div>
        {!creating && <button onClick={() => { setCreating(true); setEditing(null); setCreds(null); }} className="px-4 py-2 rounded-lg text-white text-sm font-semibold shrink-0" style={{ backgroundColor: BRAND }}>+ New office user</button>}
      </div>
      <div className="p-5 space-y-4">
        {error && <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2">{error}</div>}
        {notice && <div className="text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg px-3 py-2">{notice}</div>}
        {creds && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm space-y-2">
            <div className="font-bold text-amber-900">Login created for {creds.name} — password shown once</div>
            <div className="font-mono bg-white border rounded-lg p-3">Portal: {window.location.origin}<br />Email: {creds.email}<br />Temporary password: {creds.password}</div>
            <div className="text-xs text-amber-900">{creds.emailed ? "A welcome email with these details was also sent." : "Email isn't configured, so share these details by phone or in person."}</div>
            <button onClick={() => setCreds(null)} className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold" style={{ backgroundColor: BRAND }}>Done</button>
          </div>
        )}

        {creating && (
          <form onSubmit={create} className="border rounded-xl p-4 space-y-4">
            <label className="block text-sm">Give office access to an existing caregiver login (optional)
              <select value={form.from_caregiver} onChange={(e) => setForm({ ...form, from_caregiver: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 bg-white">
                <option value="">No — create a new login</option>
                {(options?.caregivers || []).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email}{c.employee_id ? "" : " (not linked to an employee record)"}</option>)}
              </select>
              <span className="block text-xs text-gray-500 mt-1">They keep the same email and password, keep their caregiver portal, and gain the office pages you tick below.</span>
            </label>
            {!form.from_caregiver && <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-sm">Full name<input required={!form.from_caregiver} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
              <label className="text-sm">Email<input required={!form.from_caregiver} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
              <label className="text-sm">Phone (optional)<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
            </div>}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500">Start from a template:</span>
              {Object.keys(PRESETS).map((k) => <button type="button" key={k} onClick={() => setForm({ ...form, permissions: { ...PRESETS[k] } })} className="px-2.5 py-1 rounded-full border font-semibold" style={{ borderColor: BRAND, color: BRAND }}>{k}</button>)}
            </div>
            <PermissionGrid registry={registry} value={form.permissions} onChange={(permissions) => setForm({ ...form, permissions })} />
            {!form.from_caregiver && <CaregiverHat options={options} value={form} onChange={(v) => setForm({ ...form, also_caregiver: v.also_caregiver, employee_id: v.employee_id })} />}
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">{summary(registry, form.permissions)}</span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
                <button disabled={busy} className="px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>{busy ? "Creating…" : "Create login"}</button>
              </div>
            </div>
          </form>
        )}

        {users.length === 0 && !creating ? <p className="text-sm text-gray-500">No office staff logins yet.</p> : (
          <div className="divide-y border rounded-xl">
            {users.map((u) => (
              <div key={u.id} className="p-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">{u.name} {u.account_status !== "Active" && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">Disabled</span>}</div>
                    <div className="text-xs text-gray-500">{u.email} · {summary(registry, u.permissions)}{u.also_caregiver && u.employee_id ? <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">+ caregiver ({u.employee_name || u.employee_id})</span> : null}</div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs font-semibold">
                    <button onClick={() => { setEditing(editing?.id === u.id ? null : { id: u.id, name: u.name, permissions: { ...u.permissions }, also_caregiver: u.also_caregiver, employee_id: u.employee_id || "" }); setCreating(false); }} className="underline" style={{ color: BRAND }}>{editing?.id === u.id ? "Close" : "Edit access"}</button>
                    <button onClick={async () => { try { const r = await api.access.resetPassword(u.id); setCreds({ name: u.name, email: u.email, password: r.temp_password, emailed: false }); } catch (e) { setError(e.message); } }} className="underline" style={{ color: BRAND }}>New password</button>
                    <button onClick={async () => { if (u.account_status === "Active" && !window.confirm(`Disable ${u.name}'s login now?`)) return; await api.access.setStatus(u.id, u.account_status === "Active" ? "Disabled" : "Active"); load(); }} className={`underline ${u.account_status === "Active" ? "text-red-700" : "text-emerald-700"}`}>{u.account_status === "Active" ? "Disable" : "Enable"}</button>
                  </div>
                </div>
                {editing?.id === u.id && (
                  <div className="mt-3 space-y-3">
                    <PermissionGrid registry={registry} value={editing.permissions} onChange={(permissions) => setEditing({ ...editing, permissions })} />
                    <CaregiverHat options={options} currentEmployeeId={u.employee_id} value={editing} onChange={(v) => setEditing({ ...editing, also_caregiver: v.also_caregiver, employee_id: v.employee_id })} />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
                      <button disabled={busy} onClick={saveEdit} className="px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>Save access</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">Only full administrators can create users or change access. Office staff never see Users &amp; Access, so they cannot give themselves more access.</p>
      </div>
    </section>
  );
}
